import Axios, { type AxiosRequestConfig } from 'axios';
import {
    type HubConnection,
    HubConnectionBuilder,
    HubConnectionState,
    type ISubscription,
} from '@microsoft/signalr';
import { Repeater } from '@repeaterjs/repeater';
import TripPackageLoader, {
    type TripPackageLoaderResult,
} from '../TripPackageLoader';
import FlightPackageLoaderResultFactory from './FlightPackageLoaderResultFactory';
import { type FlightPackageLoaderConfig } from './types';
import { NetworkError } from '@itinerary/ItineraryModuleTypes';
import { RequestCanceledException } from '@utils/itinerary';
import { FlightServiceException } from '@itinerary/trip/flight/FlightModuleTypes';
import {
    CabinClassRequest,
    FlightsApiApiFlightsPost,
    type GetFlightsRequestDto,
    type GetFlightsResponseDto,
} from '@clients/FlightApi';
import type FlightPackage from '@itinerary/trip/flight/FlightPackage';

export class FlightPackageLoader extends TripPackageLoader<FlightPackage> {
    private readonly _cabinClass?: CabinClassRequest;
    private readonly _returnDate?: Date;
    private readonly _adultCount: number;
    private readonly _childCount: number;
    private readonly _infantCount: number;
    private readonly _token?: string;
    private readonly _xplayerID?: string;
    private _isCapacityTrackCampaign = false;
    private _isRealTime = false;
    private hubConnection: HubConnection | null = null;
    private subscribe: ISubscription<any> | null = null;

    private cancelGetAvailableRealTime() {
        this.subscribe?.dispose();
        this.hubConnection?.stop();
    }

    override get isRealTime(): boolean {
        return this._isRealTime;
    }

    private async *getAvailableRealTime(
        request: GetFlightsRequestDto
    ): AsyncGenerator<GetFlightsResponseDto> {
        if (
            !this.hubConnection ||
            this.hubConnection.state === HubConnectionState.Disconnected ||
            this.hubConnection.state === HubConnectionState.Disconnecting
        ) {
            this.hubConnection = new HubConnectionBuilder()
                .withUrl(`${useBaseUrls().flight}/flightsHub`, {
                    accessTokenFactory: () => this._token ?? '',
                    headers: {
                        'X-PlayerID': this._xplayerID ?? '',
                    },
                })
                .build();
            await this.hubConnection.start();
        }
        const hubStream = this.hubConnection.stream('GetAvailable', request);
        const responses = new Repeater<GetFlightsResponseDto>(
            async (push, stop) => {
                this.subscribe = hubStream.subscribe({
                    next: (res: GetFlightsResponseDto) => {
                        push(res);
                    },
                    complete: () => {
                        this.subscribe?.dispose();
                        stop();
                    },
                    error: (err: any) => {
                        this.subscribe?.dispose();
                        this.hubConnection?.stop();
                        stop(new Error(err));
                    },
                });
                await stop;
                this.subscribe?.dispose();
                this.hubConnection?.stop();
            }
        );
        for await (const response of responses) {
            yield response;
        }
    }

    constructor({
        originCode,
        destinationCode,
        date,
        adultCount,
        childCount,
        infantCount,
        cabinClass,
        returnDate,
        url,
        originName,
        destinationName,
        isRealTime,
        isCapacityTrackCampaign,
        token,
        xplayerID,
    }: FlightPackageLoaderConfig) {
        super({
            totalSeatCount: adultCount + childCount,
            destinationCode,
            destinationName,
            originCode,
            originName,
            date,
            url,
        });
        this._adultCount = adultCount;
        this._childCount = childCount;
        this._infantCount = infantCount;
        this._cabinClass = cabinClass;
        this._returnDate = returnDate;
        this._token = token;
        this._xplayerID = xplayerID;
        this._isRealTime = !!isRealTime;
        // noinspection PointlessBooleanExpressionJS
        this._isCapacityTrackCampaign = !!isCapacityTrackCampaign;
    }

    get adultCount() {
        return this._adultCount;
    }

    get childCount() {
        return this._childCount;
    }

    get infantCount() {
        return this._infantCount;
    }

    override compareTo(other: FlightPackageLoaderConfig): boolean {
        if (this._originCode !== other.originCode) {
            return false;
        }
        if (this._destinationCode !== other.destinationCode) {
            return false;
        }
        if (this._adultCount !== other.adultCount) {
            return false;
        }
        if (this._childCount !== other.childCount) {
            return false;
        }
        if (this._infantCount !== other.infantCount) {
            return false;
        }
        if (this._cabinClass !== other.cabinClass) {
            return false;
        }
        if (this.date.toString() !== other.date.toString()) {
            return false;
        }
        // noinspection RedundantIfStatementJS
        if (this._returnDate?.toString() !== other.returnDate?.toString()) {
            return false;
        }
        return true;
    }

    override stopLoading(): void {
        if (!this.isRealTime) {
            super.stopLoading();
        } else {
            this.cancelGetAvailableRealTime();
        }
    }

    private async getAvailable(
        request: GetFlightsRequestDto,
        params: AxiosRequestConfig = {}
    ) {
        try {
            const { data, status } = await FlightsApiApiFlightsPost(
                {
                    getFlightsRequestDto: request,
                },
                params
            );
            if (status === 200) {
                return data;
            } else {
                // @warn suspicious behavior
                throw new FlightServiceException((data as any).Message);
            }
        } catch (e) {
            if (Axios.isCancel(e)) {
                throw new RequestCanceledException();
            } else if ((e as any).message === 'Network Error') {
                throw new NetworkError();
            } else {
                throw new FlightServiceException('خطایی رخ داد!');
            }
        }
    }

    override async *_getPackages(
        axiosParams?: any
    ): AsyncGenerator<TripPackageLoaderResult<FlightPackage>> {
        const routes = [
            {
                OriginCode: this._originCode,
                DestinationCode: this._destinationCode,
                DepartureDate: dayConfig.gregory(this.date).format(formatter()),
            },
        ];

        if (this._returnDate) {
            routes.push({
                OriginCode: this._destinationCode,
                DestinationCode: this._originCode,
                DepartureDate: dayConfig
                    .gregory(this._returnDate)
                    .format(formatter()),
            });
        }
        const request: GetFlightsRequestDto = {
            AdultCount: this._adultCount,
            ChildCount: this._childCount,
            InfantCount: this._infantCount,
            CabinClass: this._cabinClass ?? CabinClassRequest.All,
            Routes: routes,
            Baggage: true,
        };
        if (this.isRealTime) {
            try {
                const gen = this.getAvailableRealTime(request);
                for await (const response of gen) {
                    yield this.mapResponse(response);
                }
            } catch (error) {
                reportException(error);
                const result = await this.getAvailable(request, axiosParams);
                yield this.mapResponse(result);
            }
        } else {
            const result = await this.getAvailable(request, axiosParams);
            yield this.mapResponse(result);
        }
    }

    private mapResponse(
        result: GetFlightsResponseDto
    ): TripPackageLoaderResult<FlightPackage> {
        return new FlightPackageLoaderResultFactory({
            apiResult: result,
            adultCount: this._adultCount,
            childCount: this._childCount,
            infantCount: this._infantCount,
            isCapacityTrackCampaign: this._isCapacityTrackCampaign,
        }).loaderResult;
    }
}
